import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faSave,
  faSpinner,
  faChevronDown,
  faChevronUp,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import {
  productParametersService,
  validateParameterValue,
  getParameterTypeLabel,
} from "../services/productParameters";
import { flattenFields, computeCalculatedFields } from "../utils/parameterTree";

// Secções que contêm um campo com erro devem começar abertas para o
// utilizador ver o problema sem precisar de expandir tudo manualmente.
const findGroupIdsWithErrors = (nodes, errorIds) => {
  const groupIds = new Set();
  const walk = (list) => {
    let hasError = false;
    for (const node of list || []) {
      if (node.type === "group") {
        const childHasError = walk(node.children);
        if (childHasError) {
          groupIds.add(node.id);
          hasError = true;
        }
      } else if (errorIds.has(node.id)) {
        hasError = true;
      }
    }
    return hasError;
  };
  walk(nodes);
  return groupIds;
};

const ParameterFormModal = ({
  isOpen,
  onClose,
  productId,
  productName,
  productionOrderItemId,
  productionLotId = null, // Lote físico já produzido: ao salvar, aplica a alteração diretamente nele
  existingResponses = null,
  onSave,
  readOnly = false, // Modo somente leitura
}) => {
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Carregar template: se já temos respostas existentes (ex.: visualização de
  // um lote já preenchido), usar a versão do template gravada junto com essa
  // resposta — não a versão ATIVA hoje, que pode já ter mudado desde então e
  // teria ids de campo diferentes, quebrando a exibição dos valores antigos.
  useEffect(() => {
    if (!isOpen) return;
    if (existingResponses?.product_parameter_templates) {
      setTemplate(existingResponses.product_parameter_templates);
      return;
    }
    if (productId) {
      fetchTemplate();
    }
  }, [isOpen, productId, existingResponses]);

  // Carregar respostas existentes
  useEffect(() => {
    if (existingResponses) {
      setResponses(existingResponses.responses || {});
    } else {
      setResponses({});
    }
  }, [existingResponses]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const data = await productParametersService.getActiveTemplate(productId);
      setTemplate(data);

      // Inicializar respostas vazias se não houver existentes (percorre
      // secções/grupos aninhados; campos calculados não precisam de valor
      // inicial, são derivados dos operandos)
      if (!existingResponses && data?.parameters) {
        const initialResponses = {};
        flattenFields(data.parameters).forEach((param) => {
          if (param.type === "calculated") return;
          if (param.type === "checkbox") {
            initialResponses[param.id] = false;
          } else if (param.type === "multi_select") {
            initialResponses[param.id] = [];
          } else {
            initialResponses[param.id] = "";
          }
        });
        setResponses(initialResponses);
      }
    } catch (error) {
      console.error("Erro ao buscar template:", error);
      toast.error("Erro ao carregar formulário de parâmetros");
    } finally {
      setLoading(false);
    }
  };

  // Atualizar valor de resposta
  const handleChange = (paramId, value) => {
    setResponses((prev) => ({
      ...prev,
      [paramId]: value,
    }));

    // Limpar erro do campo
    if (errors[paramId]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[paramId];
        return newErrors;
      });
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Valores exibidos: iguais às respostas, exceto campos calculados, cujo
  // valor é sempre derivado ao vivo dos operandos (o backend recalcula
  // outra vez antes de persistir, então não precisamos guardar em estado).
  const displayResponses = template
    ? computeCalculatedFields(template.parameters, responses)
    : responses;

  // Validar e salvar
  const handleSave = async () => {
    if (!template) return;

    const leafFields = flattenFields(template.parameters);
    const newErrors = {};
    leafFields.forEach((param) => {
      if (param.type === "calculated") return; // sempre derivado, nunca digitado
      const validation = validateParameterValue(param, responses[param.id]);
      if (!validation.valid) {
        newErrors[param.id] = validation.error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const groupsToExpand = findGroupIdsWithErrors(
        template.parameters,
        new Set(Object.keys(newErrors))
      );
      setExpandedGroups((prev) => {
        const next = { ...prev };
        groupsToExpand.forEach((id) => {
          next[id] = true;
        });
        return next;
      });
      toast.error("Por favor, corrija os erros no formulário");
      return;
    }

    try {
      setSaving(true);
      await productParametersService.saveResponses({
        production_order_item_id: productionOrderItemId,
        template_id: template.id,
        responses,
        production_lot_id: productionLotId,
      });

      toast.success(
        productionLotId
          ? "Parâmetros do lote atualizados com sucesso!"
          : "Parâmetros salvos com sucesso!"
      );
      onSave && onSave(responses);
      onClose();
    } catch (error) {
      console.error("Erro ao salvar respostas:", error);
      toast.error(error.message || "Erro ao salvar parâmetros");
    } finally {
      setSaving(false);
    }
  };

  // Renderizar campo baseado no tipo
  const renderField = (param) => {
    const value = responses[param.id];
    const error = errors[param.id];
    const baseInputClass = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
      error
        ? "border-red-300 focus:ring-red-400"
        : "border-neutral-300 focus:ring-neutral-400"
    } ${readOnly ? "bg-neutral-100 cursor-not-allowed" : ""}`;

    switch (param.type) {
      case "text":
        return (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => handleChange(param.id, e.target.value)}
            className={baseInputClass}
            placeholder={`Digite ${param.name.toLowerCase()}`}
            disabled={readOnly}
          />
        );

      case "integer":
        return (
          <input
            type="number"
            value={value || ""}
            onChange={(e) => handleChange(param.id, e.target.value)}
            className={baseInputClass}
            placeholder={`Digite ${param.name.toLowerCase()}`}
            min={param.min}
            max={param.max}
            step="1"
            disabled={readOnly}
          />
        );

      case "float":
        return (
          <input
            type="number"
            value={value || ""}
            onChange={(e) => handleChange(param.id, e.target.value)}
            className={baseInputClass}
            placeholder={`Digite ${param.name.toLowerCase()}`}
            min={param.min}
            max={param.max}
            step="0.01"
            disabled={readOnly}
          />
        );

      case "checkbox":
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              id={`param-${param.id}`}
              checked={value || false}
              onChange={(e) => handleChange(param.id, e.target.checked)}
              className={`h-5 w-5 text-neutral-900 rounded border-neutral-300 ${readOnly ? "cursor-not-allowed" : ""}`}
              disabled={readOnly}
            />
            <label
              htmlFor={`param-${param.id}`}
              className="ml-2 text-neutral-700"
            >
              Sim
            </label>
          </div>
        );

      case "single_select":
        return (
          <div className="space-y-2">
            {param.options?.map((option, index) => (
              <label
                key={index}
                className={`flex items-center p-2 rounded ${readOnly ? "cursor-not-allowed" : "cursor-pointer hover:bg-neutral-50"}`}
              >
                <input
                  type="radio"
                  name={`param-${param.id}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleChange(param.id, e.target.value)}
                  className="h-4 w-4 text-neutral-900 border-neutral-300"
                  disabled={readOnly}
                />
                <span className="ml-2 text-neutral-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case "multi_select":
        return (
          <div className="space-y-2">
            {param.options?.map((option, index) => (
              <label
                key={index}
                className={`flex items-center p-2 rounded ${readOnly ? "cursor-not-allowed" : "cursor-pointer hover:bg-neutral-50"}`}
              >
                <input
                  type="checkbox"
                  checked={Array.isArray(value) && value.includes(option)}
                  onChange={(e) => {
                    if (readOnly) return;
                    const currentValues = Array.isArray(value) ? value : [];
                    if (e.target.checked) {
                      handleChange(param.id, [...currentValues, option]);
                    } else {
                      handleChange(
                        param.id,
                        currentValues.filter((v) => v !== option)
                      );
                    }
                  }}
                  className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
                  disabled={readOnly}
                />
                <span className="ml-2 text-neutral-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case "date":
        return (
          <input
            type="date"
            value={value || ""}
            onChange={(e) => handleChange(param.id, e.target.value)}
            className={baseInputClass}
            disabled={readOnly}
          />
        );

      case "datetime":
        return (
          <input
            type="datetime-local"
            value={value || ""}
            onChange={(e) => handleChange(param.id, e.target.value)}
            className={baseInputClass}
            disabled={readOnly}
          />
        );

      case "time":
        return (
          <input
            type="time"
            value={value || ""}
            onChange={(e) => handleChange(param.id, e.target.value)}
            className={baseInputClass}
            disabled={readOnly}
          />
        );

      case "calculated":
        return (
          <input
            type="text"
            value={displayResponses[param.id] ?? ""}
            readOnly
            className="w-full px-3 py-2 border border-neutral-200 rounded-lg bg-neutral-100 text-neutral-600 cursor-not-allowed"
          />
        );

      default:
        return (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => handleChange(param.id, e.target.value)}
            className={baseInputClass}
            disabled={readOnly}
          />
        );
    }
  };

  // Renderiza um nó da árvore: campo comum, campo calculado ou secção
  // (accordion) com os seus próprios filhos, recursivamente.
  const renderNode = (node) => {
    if (node.type === "group") {
      const isExpanded = !!expandedGroups[node.id];
      const sortedChildren = [...(node.children || [])].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      return (
        <div
          key={node.id}
          className="border border-neutral-200 rounded-lg overflow-hidden"
        >
          <button
            type="button"
            onClick={() => toggleGroup(node.id)}
            className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 text-left"
          >
            <span className="flex items-center font-medium text-neutral-800">
              <FontAwesomeIcon icon={faLayerGroup} className="mr-2 text-neutral-400" />
              {node.name || "(Secção)"}
            </span>
            <FontAwesomeIcon
              icon={isExpanded ? faChevronUp : faChevronDown}
              className="text-neutral-500"
            />
          </button>
          {isExpanded && (
            <div className="p-4 space-y-6 bg-white">
              {sortedChildren.map(renderNode)}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={node.id}>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          {node.name}
          {node.required && <span className="text-red-500 ml-1">*</span>}
          <span className="text-xs text-neutral-400 ml-2">
            ({getParameterTypeLabel(node.type)})
          </span>
        </label>

        {(node.type === "integer" || node.type === "float") &&
          (node.min !== undefined || node.max !== undefined) && (
            <p className="text-xs text-neutral-500 mb-2">
              {node.min !== undefined && `Mín: ${node.min}`}
              {node.min !== undefined && node.max !== undefined && " | "}
              {node.max !== undefined && `Máx: ${node.max}`}
            </p>
          )}

        {renderField(node)}

        {errors[node.id] && (
          <p className="text-sm text-red-500 mt-1">{errors[node.id]}</p>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-neutral-800">
              {readOnly
                ? "Parâmetros de Produção (Visualização)"
                : productionLotId
                ? "Editar Parâmetros do Lote"
                : "Parâmetros de Produção"}
            </h3>
            <p className="text-sm text-neutral-500">{productName}</p>
            {!readOnly && productionLotId && (
              <p className="text-xs text-amber-600 mt-1">
                Este lote já foi produzido. Ao salvar, as alterações são aplicadas diretamente nele.
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 p-2"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin text-2xl text-neutral-400"
              />
              <span className="ml-2 text-neutral-500">
                Carregando formulário...
              </span>
            </div>
          ) : !template ? (
            <div className="text-center py-12 text-neutral-500">
              <p>Nenhum template de parâmetros configurado para este produto.</p>
              <p className="text-sm mt-2">
                Configure os parâmetros na área de administração.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {[...(template.parameters || [])]
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(renderNode)}
            </div>
          )}
        </div>

        {/* Footer */}
        {template && (
          <div className="p-4 border-t border-neutral-200 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 border border-neutral-300 rounded-lg hover:bg-neutral-50"
            >
              {readOnly ? "Fechar" : "Cancelar"}
            </button>
            {!readOnly && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <>
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="animate-spin mr-2"
                    />
                    Salvando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Salvar
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParameterFormModal;
